import type { CoreError } from '@ingenico/api-sdk-core/common';
import { LNGS_NAMESPACE_LOGICAL_DEVICE } from '@ingenico/util-i18n-config';
import { Level, showNotification } from '@ingenico/react-notifications';
import { useBusinessService } from '@ingenico/react-section-utils';
import { ModalConfirmationDialog } from '@ingenico/react-ui/confirmation-dialog';
import { requestAndUpdateState } from '@ingenico/react-utils/functions';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import { isEmpty } from 'lodash-es';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';

export interface UseCancelDeviceLogFileProps {
  readonly refetch?: () => void;
}

export interface UseCancelDeviceLogFileReturn {
  readonly cancelTrigger: (deviceLogId: string) => void;
  readonly cancelModal: ReactNode;
}

export function useCancelDeviceLogFile({ refetch }: UseCancelDeviceLogFileProps): UseCancelDeviceLogFileReturn {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_LOGICAL_DEVICE));

  const [currentDeviceLogId, setCurrentDeviceLogId] = useState('');

  const onTrigger = useCallback((deviceLogId: string) => {
    setCurrentDeviceLogId(deviceLogId);
  }, []);

  const onCancel = useCallback(() => {
    setCurrentDeviceLogId('');
  }, []);

  const deviceManagementService = useBusinessService(DeviceManagementService);

  const [cancelDeviceLogState, setCancelDeviceLog] = useAsyncDataState<unknown, CoreError>();

  const onError = useCallback(() => {
    showNotification({
      content: t('cancelDeviceLogs.error'),
      level: Level.Error,
    });
    onCancel();
  }, [onCancel, t]);

  const onConfirm = useCallback(() => {
    requestAndUpdateState(
      deviceManagementService.cancelLogRequest({ deviceLogId: currentDeviceLogId }),
      setCancelDeviceLog,
      {
        onSuccess() {
          showNotification({
            content: t('cancelDeviceLogs.success'),
            level: Level.Success,
          });
          refetch?.();
          onCancel();
        },
        onError,
      },
    );
  }, [currentDeviceLogId, onCancel, onError, deviceManagementService, refetch, setCancelDeviceLog, t]);

  const { isLoading } = cancelDeviceLogState;

  const cancelModal = useMemo(
    () => (
      <ModalConfirmationDialog
        cancelLabel={t('cancelDeviceLogs.cancel')}
        confirmLabel={t('cancelDeviceLogs.confirm')}
        i18nNamespaces={[]}
        isConfirmLoading={isLoading}
        isOpen={!isEmpty(currentDeviceLogId)}
        onClose={onCancel}
        onConfirm={onConfirm}
        title={t('cancelDeviceLogs.title')}
        caption={t('cancelDeviceLogs.content')}
      />
    ),
    [currentDeviceLogId, isLoading, onCancel, onConfirm, t],
  );

  return {
    cancelTrigger: onTrigger,
    cancelModal,
  };
}
